import { useInfiniteQuery } from 'react-query';
import { useRouter } from 'next/router';

import { useOAIQuery } from '~/hooks/reactQuery';
import { fetchOAIData } from '~/libs/openapi';
import type { OAIParameters, OAISchema } from '~/types/openapi';

export const useShowcases = (
  code: string,
  variables: Record<string, unknown> = {},
  enabled = true,
) => {
  const { data, ...props } = useOAIQuery({
    queryKey: '/api/market-place/v4/showcases',
    variables: { code, ...variables },
    queryOptions: {
      enabled: !!enabled,
      keepPreviousData: true,
    },
  });

  return { data, ...props };
};

type ShowcaseQueryOptions = {
  size?: number;
  params?: Record<string, unknown>;
  queryOptions?: Record<string, unknown>;
};

export const useInfiniteShowcaseQuery = ({
  size = 20,
  params = {},
  queryOptions,
}: ShowcaseQueryOptions = {}) => {
  const router = useRouter();

  const variables: OAIParameters<'/api/market-place/v4/showcases', 'get'> = {
    ...router.query,
  };

  const infiniteQuery = useInfiniteQuery(
    ['/api/market-place/v4/showcases', size, params, variables],
    async ({ signal, pageParam }) => {
      const data = await fetchOAIData<
        OAISchema['MarketShowcaseProductsResponseV3']
      >({
        queryKey: '/api/market-place/v4/showcases',
        variables: {
          ...variables,
          ...((pageParam || {}) as Record<string, unknown>),
        },
        fetchOptions: {
          signal,
        },
      });

      return {
        ...data,
        count: data.count,
        products: data.products,
      };
    },
    {
      ...queryOptions,
      keepPreviousData: true,
      enabled: !!variables.code,
      getNextPageParam: (param) => {
        const { hasNextPage, sortType, searchAfter } = param.products as {
          content: OAISchema['MarketShowcaseProductsResponseV3'][];
          hasNextPage: boolean;
          size: number;
          sortType: string;
          searchAfter: string[];
        };

        if (!hasNextPage) {
          return undefined;
        }

        return { sortType, searchAfter };
      },
    },
  );

  const fetchNextPage = () => {
    if (infiniteQuery.isFetching || !infiniteQuery.hasNextPage) {
      return;
    }

    void infiniteQuery.fetchNextPage();
  };

  const { pages, count } = (() => {
    const list = infiniteQuery.data
      ?.pages as OAISchema['MarketShowcaseProductsResponseV3'][];

    return {
      pages: list?.map(({ products }) => products?.content).flat(),
      count: list?.[0]?.count || 0,
    };
  })();

  return {
    ...infiniteQuery,
    pages,
    count,
    fetchNextPage,
  };
};
